import { useInfiniteQuery } from '@tanstack/react-query';
import { OrdersSalesQueryKeys } from '@enums/react-query-keys';
import { OrderData } from '@interfaces/api/responses/my-order';
import { ResponseResult, getOrders } from '@services/my-orders-sales-service';
import useUser from '@hooks/user/use-user';
import { RESULT_PER_PAGE } from '@constants/orders-sales';

const DEFAULT_ORDER: OrderData = {
  orders: [],
  hasMore: null,
};
const DEFAULT: ResponseResult<OrderData> = { kind: 'Success', data: DEFAULT_ORDER };

function mergeOrderData(results: ResponseResult<OrderData>[], bookmarkOrderId?: string): OrderData {
  return results.reduce((data: OrderData, result: ResponseResult<OrderData>, currentIdx) => {
    if (result.kind !== 'Success') {
      return data;
    }
    const orders =
      currentIdx && bookmarkOrderId
        ? result.data.orders?.filter((o) => o.orderId !== bookmarkOrderId)
        : result.data.orders;
    return {
      hasMore: result.data.hasMore ?? result.data.orders.length >= RESULT_PER_PAGE,
      orders: [...data.orders, ...(orders || [])],
    };
  }, DEFAULT_ORDER);
}

const useOrdersQuery = (bookmarkOrderId?: string, bookmarkProductId?: string) => {
  const { isAuthenticated, userId } = useUser();

  const { error, isFetching, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [OrdersSalesQueryKeys.ORDERS, userId, isAuthenticated, bookmarkOrderId, bookmarkProductId],
    queryFn: ({ pageParam = 1 }) => {
      if (pageParam === 1) {
        return getOrders(Number(pageParam), bookmarkProductId, bookmarkOrderId);
      }
      return getOrders(Number(pageParam));
    },
    getNextPageParam: (_, pages) => {
      return pages.length + 1;
    },
    staleTime: Infinity,
    enabled: isAuthenticated,
    placeholderData: { pages: [DEFAULT], pageParams: [undefined] },
  });

  const hasData = data.pages[0].kind === 'Success' && data.pages[0]?.data?.orders?.[0] != null;
  const isErrorOrder = error != null || data.pages.find(({ kind }) => kind !== 'Success') != null;
  const isLoadingOrder = isFetching && !hasData;
  const isLoadingMoreOrder = isFetching && hasData;
  const isEmptyOrder = !isLoadingOrder && !hasData;
  const orderData = mergeOrderData(data.pages, bookmarkOrderId);

  return { isErrorOrder, isLoadingOrder, isLoadingMoreOrder, isEmptyOrder, orderData, fetchOrder: fetchNextPage };
};

export default useOrdersQuery;
